import { supabase } from '../../shared/supabase/supabaseClient';
import { createKeyedTtlCache } from '../../shared/data/keyedTtlCache';
import { registerStorefrontReadModelInvalidator } from '../../shared/ports/readModelInvalidationPort';
import { registerSessionCacheClearHandler } from '../../shared/ports/sessionResetPort';
import {
  parseBusinessHours,
  type BusinessHourDay,
  type ManualOverride,
} from '../../settings/domain/storeProfileTypes';

/**
 * Read model consolidado de Home (`home_snapshot` RPC).
 * Una llamada cubre ventas del día, caja, badges, storefront, agenda y tareas.
 * Contrato: documentacion/ingenieria/network/home-screen-requirements.md §5.
 *
 * Cache: TTL corto para deduplicar el fan-out de mount (load hook + providers +
 * evidencias día-1 leen lo mismo). Los refrescos interactivos pasan force:true —
 * la entrada cacheada existe para lectores oportunistas, no para servir stale.
 */

const SNAPSHOT_TTL_MS = 15_000;

export type HomeSnapshotSales = { total: number; count: number };

export type HomeSnapshotCashSession = {
  id: string;
  openedAt: string;
  openingAmount: number;
};

export type HomeSnapshotBadges = {
  ordersPending: number;
  complaintsOpen: number;
  orderUnreadConversations: number;
  consultationsUnread: number;
  quotesUnread: number;
  supportUnread: number;
};

export type HomeSnapshotStorefront = {
  isPublic: boolean;
  slug: string | null;
  isOnDuty: boolean;
  manualOverride: ManualOverride;
  manualOverrideUntil: string | null;
  businessHours: BusinessHourDay[];
  deliveryEnabled: boolean;
  ownDeliveryEnabled: boolean;
  vueltoRidersEnabled: boolean;
  /** Pedidos online lifetime (veredicto "setup online completo" del gating). */
  onlineOrdersCount: number | null;
  /** deliveryEnabled=false, o zonas/propio/riders configurados. */
  deliveryReady: boolean | null;
};

export type HomeSnapshotAgendaNext = {
  startsAt: string;
  customerName: string | null;
};

export type HomeSnapshotAgenda = {
  activeCount: number;
  pendingCount: number;
  next: HomeSnapshotAgendaNext | null;
};

export type HomeSnapshot = {
  generatedAt: string | null;
  salesToday: HomeSnapshotSales | null;
  cashSession: HomeSnapshotCashSession | null;
  badges: HomeSnapshotBadges | null;
  storefront: HomeSnapshotStorefront | null;
  agendaToday: HomeSnapshotAgenda | null;
  pendingTasks: number | null;
  lifetimeSales: number | null;
  /** Turnos no cancelados lifetime (evidencia del hito día 1 con appointments). */
  lifetimeAppointments: number | null;
};

const asObj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

const asNum = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const asStr = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function parseStorefront(raw: unknown): HomeSnapshotStorefront | null {
  const o = asObj(raw);
  if (!o) return null;
  const override = asStr(o.manual_override);
  return {
    isPublic: o.is_public === true,
    slug: asStr(o.slug),
    isOnDuty: o.is_on_duty === true,
    manualOverride:
      override === 'force_open' || override === 'force_close' ? override : 'auto',
    manualOverrideUntil: asStr(o.manual_override_until),
    businessHours: parseBusinessHours(o.business_hours),
    deliveryEnabled: o.delivery_enabled === true,
    ownDeliveryEnabled: o.own_delivery_enabled === true,
    vueltoRidersEnabled: o.vuelto_riders_enabled === true,
    onlineOrdersCount: asNum(o.online_orders_count),
    deliveryReady: typeof o.delivery_ready === 'boolean' ? o.delivery_ready : null,
  };
}

export function parseHomeSnapshot(data: unknown): HomeSnapshot | null {
  const root = asObj(data);
  if (!root) return null;

  const sales = asObj(root.sales_today);
  const cash = asObj(root.cash_session);
  const badges = asObj(root.badges);
  const agenda = asObj(root.agenda_today);
  const next = asObj(agenda?.next);

  return {
    generatedAt: asStr(root.generated_at),
    salesToday: sales
      ? { total: asNum(sales.total) ?? 0, count: asNum(sales.count) ?? 0 }
      : null,
    cashSession:
      cash && asStr(cash.id) && asStr(cash.opened_at)
        ? {
            id: asStr(cash.id)!,
            openedAt: asStr(cash.opened_at)!,
            openingAmount: asNum(cash.opening_amount) ?? 0,
          }
        : null,
    badges: badges
      ? {
          ordersPending: asNum(badges.orders_pending) ?? 0,
          complaintsOpen: asNum(badges.complaints_open) ?? 0,
          orderUnreadConversations: asNum(badges.order_unread_conversations) ?? 0,
          consultationsUnread: asNum(badges.consultations_unread) ?? 0,
          quotesUnread: asNum(badges.quotes_unread) ?? 0,
          supportUnread: asNum(badges.support_unread) ?? 0,
        }
      : null,
    storefront: parseStorefront(root.storefront),
    agendaToday: agenda
      ? {
          activeCount: asNum(agenda.active) ?? 0,
          pendingCount: asNum(agenda.pending) ?? 0,
          next: next
            ? {
                startsAt: asStr(next.starts_at) ?? '',
                customerName: asStr(next.customer_name),
              }
            : null,
        }
      : null,
    pendingTasks: asNum(root.pending_tasks),
    lifetimeSales: asNum(root.lifetime_sales),
    lifetimeAppointments: asNum(root.lifetime_appointments),
  };
}

function dayRange(now = new Date()): { from: string; to: string; dayKey: string } {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(now);
  to.setHours(23, 59, 59, 999);
  const dayKey = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  return { from: from.toISOString(), to: to.toISOString(), dayKey };
}

const cache = createKeyedTtlCache(SNAPSHOT_TTL_MS);

const cacheKey = (branchId: string) => `${branchId}:${dayRange().dayKey}`;

export async function getHomeSnapshot(
  branchId: string,
  opts?: { force?: boolean },
): Promise<HomeSnapshot | null> {
  const { from, to } = dayRange();
  return cache.get(
    cacheKey(branchId),
    async () => {
      const { data, error } = await supabase.rpc('home_snapshot', {
        p_branch_id: branchId,
        p_day_from: from,
        p_day_to: to,
      });
      if (error) throw new Error(error.message);
      return parseHomeSnapshot(data);
    },
    { force: opts?.force },
  );
}

/** Tras writes que afectan secciones del snapshot (perfil tienda, caja, etc.). */
export function invalidateHomeSnapshot(branchId?: string): void {
  if (!branchId) {
    cache.clear();
    return;
  }
  cache.invalidate(cacheKey(branchId));
}

export function clearHomeSnapshotCache(): void {
  cache.clear();
}

// Self-registration: si este módulo no se evaluó, el cache está vacío y no hay
// nada que invalidar. Writes de perfil tienda → storefrontStatusCache → port.
registerStorefrontReadModelInvalidator((branchId, scope) => {
  if (scope !== 'profile') return;
  invalidateHomeSnapshot(branchId ?? undefined);
});
registerSessionCacheClearHandler(clearHomeSnapshotCache);
